import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStatsigUser } from '@statsig/react-bindings';
import { MTRFullLogo } from '@dsny/dsny-component-library';
import { Keyboardarrowright } from '@dsny/dsny-component-library/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { logout } from 'src/features/Auth';
import { AppDispatch, RootState } from 'src/app/store';
import {
  getPostAuthRedirectPath,
  syncAuthenticatedUserSession,
} from 'src/features/Auth/Auth.utils';
import { trackEvent } from 'src/utils/segmentService';
import { AuthWrapper } from '../Auth/Auth.styles';
import { LogoMTRWrapper, Container } from '../Auth/Signin/Signin.styles';
import {
  Card,
  CardInner,
  Title,
  Subtitle,
  TextFieldWrapper,
  SearchWrapperStyles,
  SearchInputStyles,
  SearchTailingStyles,
  Table,
  Row,
  RowText,
  RowIcon,
  NoResultsRow,
  ErrorStateWrapper,
  ErrorTitle,
  RetryLink,
} from './MultiCompanyAccess.styles';
import {
  getMultiCompanyAccess,
  MultiCompanyAccessApiResponse,
  MultiCompanyAccessCompany,
  selectMultiCompanyAccessCompany,
} from './MultiCompanyAccess.thunks';
import {
  clearMultiCompanyErrorState,
  getMultiCompanyErrorState,
  MULTI_COMPANY_ERROR_STATE_EVENT,
} from './MultiCompanyAccessErrorState';

const SHOW_MULTI_COMPANY_ERROR_STATE_PREVIEW = false;

const MultiCompanyAccess: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const navigate = useNavigate();
  const { updateUserAsync } = useStatsigUser();
  const [searchWord, setSearchWord] = useState<string>('');
  const [isErrorState, setIsErrorState] = useState<boolean>(
    SHOW_MULTI_COMPANY_ERROR_STATE_PREVIEW || getMultiCompanyErrorState()
  );
  const { companiesResponse, companyContext } = useSelector<
    RootState,
    {
      companiesResponse: MultiCompanyAccessApiResponse | null;
      companyContext: RootState['auth']['companyContext'];
    }
  >((state: RootState) => ({
    companiesResponse: state.multiCompanyAccess.companiesResponse,
    companyContext: state.auth.companyContext,
  }));

  useEffect(() => {
    const syncErrorState = () => {
      setIsErrorState(
        SHOW_MULTI_COMPANY_ERROR_STATE_PREVIEW || getMultiCompanyErrorState()
      );
    };

    window.addEventListener(MULTI_COMPANY_ERROR_STATE_EVENT, syncErrorState);

    return () => {
      window.removeEventListener(
        MULTI_COMPANY_ERROR_STATE_EVENT,
        syncErrorState
      );
    };
  }, []);

  useEffect(() => {
    if (isErrorState) {
      return;
    }

    clearMultiCompanyErrorState();
    dispatch(getMultiCompanyAccess());
  }, [dispatch, isErrorState]);

  // Retry sign-in path
  const retrySigninPath = useMemo(() => {
    const params = new URLSearchParams(location.search);
    params.delete('view');

    const queryString = params.toString();

    return queryString ? `/auth/signin?${queryString}` : '/auth/signin';
  }, [location.search]);

  // Retry sign-in handler
  const handleRetrySignin = async (): Promise<void> => {
    try {
      await logout();
    } finally {
      clearMultiCompanyErrorState();
      localStorage.removeItem('PLAYMPE_AUTH');
      window.location.hash = retrySigninPath;
    }
  };

  const companies: MultiCompanyAccessCompany[] =
    companiesResponse?.data.companies ?? [];

  const filteredCompanies = useMemo(
    () =>
      companies.filter((company) =>
        company.business_name.toLowerCase().includes(searchWord.toLowerCase())
      ),
    [companies, searchWord]
  );

  // Handle company selection
  const handleCompanySelect = (companyId: number): void => {
    dispatch(selectMultiCompanyAccessCompany({ companyId }))
      .unwrap()
      .then(async () => {
        const hasSessionUser = await syncAuthenticatedUserSession({
          updateUserAsync,
        });

        if (!hasSessionUser) {
          return;
        }
        trackEvent('Company Selected', {
          source: 'login',
          selected_company_id: companyId.toString(),
          previous_company_id: String(companyContext.companyId ?? ''),
          company_count: companies.length.toString(),
        });
        navigate(getPostAuthRedirectPath('/home', location.search));
      })
      .catch(() => {
        // Keep the user on the selection page when the company switch fails.
      });
  };

  // Handle search input changes
  const handleSearch = (event: React.SyntheticEvent): void => {
    const target = event.target as HTMLInputElement;
    setSearchWord(target.value);
  };

  return (
    <AuthWrapper>
      {!isErrorState && (
        <LogoMTRWrapper>
          <MTRFullLogo />
        </LogoMTRWrapper>
      )}
      <Container>
        {isErrorState ? (
          <ErrorStateWrapper>
            <ErrorTitle>{t('MULTI_COMPANY_ERROR_TITLE')}</ErrorTitle>
            <RetryLink
              to={retrySigninPath}
              onClick={(event) => {
                event.preventDefault();
                handleRetrySignin().catch(() => undefined);
              }}
            >
              {t('MULTI_COMPANY_ERROR_RETRY')}
            </RetryLink>
          </ErrorStateWrapper>
        ) : (
          <Card>
            <CardInner>
              <Title>{t('MULTI_COMPANY_TITLE')}</Title>
              <Subtitle>{t('MULTI_COMPANY_SUBTITLE')}</Subtitle>

              <TextFieldWrapper
                id="company-search"
                name="company-search"
                setting="mtr"
                labelText=""
                placeholder={t('MULTI_COMPANY_SEARCH_PLACEHOLDER')}
                width="100%"
                tailingIcon="Search"
                onChange={handleSearch as () => void}
                wrapperStyles={{ ...SearchWrapperStyles }}
                inputStyle={{ ...SearchInputStyles }}
                tailingStyle={{ ...SearchTailingStyles }}
              />

              <Table>
                {filteredCompanies.length > 0 ? (
                  filteredCompanies.map((company) => (
                    <Row
                      key={company.company_id}
                      onClick={() => handleCompanySelect(company.company_id)}
                    >
                      <RowText>{company.business_name}</RowText>
                      <RowIcon>
                        <Keyboardarrowright style={{ width: 16, height: 16 }} />
                      </RowIcon>
                    </Row>
                  ))
                ) : (
                  <NoResultsRow>
                    <RowText>{t('MULTI_COMPANY_NO_RESULTS')}</RowText>
                  </NoResultsRow>
                )}
              </Table>
            </CardInner>
          </Card>
        )}
      </Container>
    </AuthWrapper>
  );
};

export default MultiCompanyAccess;
